// Reading the backend's per-request timing breakdown out of the (untyped) results.
//
// The backend serves this at metadata.performance, shaped as a tree rather than a flat
// bag so it states its own hierarchy: an object's total_ms is the sum of its sibling
// keys, and other_ms is whatever the named siblings do not cover.
//
//   total_ms
//   ├── queue_wait_ms          waiting for a free pipeline
//   ├── model_inference.total_ms
//   │   ├── publish_ms         feeding tensors into the graph
//   │   ├── drain_ms           blocking receive of every output — where the compute lands
//   │   └── other_ms           Python-side work neither timer wraps
//   └── other_ms               decode, annotate, encode, thread hop
//
// Flattened here into one level because the table renders it as columns. Every field is
// null when the block, the nested object, or the individual number is missing, so an
// older or misbehaving backend degrades to an em dash instead of throwing.

export type Performance = {
  totalMs: number | null;
  queueWaitMs: number | null;
  modelInferenceMs: number | null;
  publishMs: number | null;
  drainMs: number | null;
  modelOtherMs: number | null;
  otherMs: number | null;
};

export const EMPTY_PERFORMANCE: Performance = {
  totalMs: null,
  queueWaitMs: null,
  modelInferenceMs: null,
  publishMs: null,
  drainMs: null,
  modelOtherMs: null,
  otherMs: null,
};

const numberOrNull = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const objectOrNull = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : null;

export const readMetadata = (results: unknown): Record<string, unknown> | null => {
  const root = objectOrNull(results);
  return root ? objectOrNull(root.metadata) : null;
};

export const readPerformance = (results: unknown): Performance => {
  const performance = objectOrNull(readMetadata(results)?.performance);
  if (!performance) return EMPTY_PERFORMANCE;

  // Absent on an older backend, so read it through the same guard as everything else
  // rather than assuming the nesting is there.
  const model = objectOrNull(performance.model_inference) ?? {};
  return {
    totalMs: numberOrNull(performance.total_ms),
    queueWaitMs: numberOrNull(performance.queue_wait_ms),
    modelInferenceMs: numberOrNull(model.total_ms),
    publishMs: numberOrNull(model.publish_ms),
    drainMs: numberOrNull(model.drain_ms),
    modelOtherMs: numberOrNull(model.other_ms),
    otherMs: numberOrNull(performance.other_ms),
  };
};

export const readProcessingId = (results: unknown): string | null => {
  const id = readMetadata(results)?.processing_id;
  return typeof id === 'string' ? id : null;
};
